import type { CompanyReference } from "@containers/Hermes/types";

// Company names are turned into clickable profile links by rewriting the
// rendered HTML AST (hast) produced by react-markdown — NOT by string-editing
// the markdown source. Working on the parsed tree means we never corrupt
// markdown syntax and can cleanly skip code blocks and existing links.

const SKIP_TAGS = new Set(["a", "code", "pre"]);
const WORD_CHAR = /[A-Za-z0-9]/;

// Minimum name length to linkify — guards against very short/ambiguous
// master names (e.g. a 2-letter token) matching common words in prose.
const MIN_NAME_LENGTH = 3;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Company master names are typically stored ALL-CAPS; render them in title case
// for the UI (e.g. "RELIANCE INDUSTRIES LIMITED" -> "Reliance Industries Limited").
export function toTitleCase(text: string): string {
  return text.toLowerCase().replace(/\p{L}[\p{L}\p{N}]*/gu, (word) => word.charAt(0).toUpperCase() + word.slice(1));
}

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

function buildAnchor(entry: CompanyReference, linkQuery: string): HastNode {
  const base = `/companies/${entry.slug}/${entry.company_id}`;
  // Render the standardized DB name (title-cased), not the raw matched text, so
  // the link label always matches the company's profile regardless of how the
  // name appeared in the answer.
  const label = toTitleCase(entry.display || entry.name);
  return {
    type: "element",
    tagName: "a",
    // Forward the country/type (and any other) params the profile page needs.
    // They ride in from the page the user opened Hermes from; without them the
    // profile page falls back to a US/import default (wrong for other markets).
    properties: { href: linkQuery ? `${base}?${linkQuery}` : base },
    children: [{ type: "text", value: label }],
  };
}

// Split a text node's value into a mix of text + anchor nodes wherever a known
// company name appears. Returns null when nothing matched (leave node as-is).
function linkifyText(value: string, regex: RegExp, lookup: Map<string, CompanyReference>, linkQuery: string): HastNode[] | null {
  const out: HastNode[] = [];
  let lastIndex = 0;
  let matched = false;
  regex.lastIndex = 0;

  let m: RegExpExecArray | null;
  while ((m = regex.exec(value)) !== null) {
    const start = m.index;
    const end = start + m[0].length;

    // Reject partial-word hits: the chars flanking the match must not be
    // alphanumeric (so "Apple" inside "Applebees" is not linked).
    const before = start > 0 ? value[start - 1] : "";
    const after = end < value.length ? value[end] : "";
    const boundaryOk = !WORD_CHAR.test(before) && !WORD_CHAR.test(after);
    const entry = lookup.get(m[0].toLowerCase());

    if (!boundaryOk || !entry) {
      if (regex.lastIndex <= start) regex.lastIndex = start + 1;
      continue;
    }

    if (start > lastIndex) out.push({ type: "text", value: value.slice(lastIndex, start) });
    out.push(buildAnchor(entry, linkQuery));
    lastIndex = end;
    matched = true;
    if (regex.lastIndex <= start) regex.lastIndex = end;
  }

  if (!matched) return null;
  if (lastIndex < value.length) out.push({ type: "text", value: value.slice(lastIndex) });
  return out;
}

function walk(node: HastNode, blocked: boolean, regex: RegExp, lookup: Map<string, CompanyReference>, linkQuery: string): void {
  if (!node.children || node.children.length === 0) return;

  const next: HastNode[] = [];
  let changed = false;

  for (const child of node.children) {
    if (child.type === "text" && !blocked && typeof child.value === "string") {
      const replaced = linkifyText(child.value, regex, lookup, linkQuery);
      if (replaced) {
        next.push(...replaced);
        changed = true;
        continue;
      }
      next.push(child);
      continue;
    }
    if (child.type === "element") {
      walk(child, blocked || SKIP_TAGS.has(child.tagName ?? ""), regex, lookup, linkQuery);
    }
    next.push(child);
  }

  if (changed) node.children = next;
}

/**
 * Build a rehype plugin that linkifies the given companies' names within the
 * rendered answer. Longer names are matched first so "Apple Inc" wins over a
 * bare "Apple" when both are present. Returns a no-op plugin when there are no
 * linkable companies.
 */
export function makeCompanyLinkRehypePlugin(companies: CompanyReference[], linkQuery = "") {
  const usable = (companies ?? []).filter((c) => c && c.name && c.slug && c.company_id && c.name.length >= MIN_NAME_LENGTH);

  if (usable.length === 0) {
    return () => (tree: HastNode) => {
      void tree;
    };
  }

  const lookup = new Map<string, CompanyReference>();
  for (const c of usable) {
    // First entry wins for a given name; dedupe case-insensitively.
    const key = c.name.toLowerCase();
    if (!lookup.has(key)) lookup.set(key, c);
  }

  const alternation = usable
    .map((c) => c.name)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  const regex = new RegExp(`(${alternation})`, "gi");

  return () => (tree: HastNode) => walk(tree, false, regex, lookup, linkQuery);
}
